import { useEffect, useRef, useCallback, useMemo, memo } from 'react';
import { createRoot } from 'react-dom/client';
import { useDispatch, useSelector } from 'react-redux';
import { AgGridReact } from '@ag-grid-community/react';
import { ReactComponent as ColumnDragIcon } from 'images/icons/gh_dragg.svg';
import { InfiniteRowModelModule } from '@ag-grid-community/infinite-row-model';
import { useIsLastRowWithData } from 'remote-state/ticketServiceHooks';
import { useFeatureFlagQuery } from 'remote-state/featureFlagsHooks';
import { selectRB, toggleSwitchHeadersLanguage, isHeadersLanguageChange } from 'store/userSlice';
import { setSrDetailsForSummary } from 'store/ticketSummarySlice';
import { PERFORMANCE_MEASURE } from 'constants/performanceMeasure';
import { APP_CONSTANTS } from 'constants/index';
import { TICKETS_OPEN_URL } from 'constants/tickets';
import usePerformanceMeasure from 'common/utils/hooks/usePerformanceMeasure';
import PropTypes from 'prop-types';
import { FEATURE_FLAGS_KEYS } from 'constants/featureFlags';
import isEqual from 'lodash-es/isEqual';
import { GRID, COLUMN_FILTER_TYPES } from './constants';
import useTexts from './useTexts';
import CustomColumnHeader from './customColumnHeader';
import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-alpine.css';
import ActionBtnRenderer from './actionBtnRenderer';
import EndResultsMessage from './endResultsMessage';
import LoadingOverlayMessage from './loadingOverlayMessage';
import { setPrefetchSrParams } from '../slice';
import DefaultCellRender from './defaultCellRender';
import { isOneStepToNextPage } from './helpers';
import useCacheColumnsWidth from './hooks/useCacheColumnsWidth';
import { selectDirection, selectDirectionChanged, setDirectionChanged } from '../../../store/globalSlice';
import { useRbTexts } from '../../../remote-state/applicationHooks';
import { getCustomHeaderName } from './defaultCellRender/SelectRender/utils';

const SORTING_ORDER = ['desc', 'asc', null];

export const resizeLastPinnedColumn = (params) => {
  if (!params.columnApi) {
    return;
  }

  const allColumns = params.api.getColumns();
  const actualColumns = allColumns.filter((e) => e.visible);
  const lastColumn = actualColumns[actualColumns.length - 1];
  if (lastColumn.pinned) {
    const gridWidth = params.api.gridOptionsWrapper?.eGridDiv?.offsetWidth;
    const columnsBeforePinned = actualColumns.slice(0, -1);
    const columnsBeforePinnedWidth = columnsBeforePinned.reduce((total, col) => total + col.getActualWidth(), 0);
    // Stretching last column to fill empty space
    if (gridWidth > columnsBeforePinnedWidth + lastColumn.minWidth) {
      params.columnApi.setColumnWidth(lastColumn.colId, gridWidth - columnsBeforePinnedWidth);
    }
  } else {
    const pinnedColumns = actualColumns.filter((e) => e.pinned);
    const lastPinnedColumn = actualColumns[pinnedColumns.length - 1];

    // Reset streched column width
    if (lastPinnedColumn?.actualWidth !== lastPinnedColumn?.minWidth) {
      params.columnApi.setColumnWidth(lastPinnedColumn.colId, lastPinnedColumn.minWidth);
    }
  }
};

const getFirstRowSrId = (firstRow) => {
  if (!firstRow?.data) return null;
  return firstRow.data.find((col) => col.field === 'id')?.value;
};

const createClassicUrl = (srId) => (srId ? TICKETS_OPEN_URL.CLASSIC(srId) : null);

export const processFirstDataRendered = (params, callback) => {
  if (!callback) return;

  const firstRow = params.api.getDisplayedRowAtIndex(0);
  const srId = getFirstRowSrId(firstRow);
  const url = createClassicUrl(srId);

  if (srId) {
    callback({ ...params, firstRowSrId: srId, classicUrl: url });
  } else {
    callback(params);
  }
};

const useGridHandlers = (props) =>
  useMemo(
    () => ({
      onRowSelected: props.onRowSelected,
      onCellClicked: props.onCellClicked,
      onRowClicked: props.onRowClicked,
      onColumnMoved: props.onColumnMoved,
      onFilterOpened: props.onFilterOpened,
      tabToNextCell: props.tabToNextCell,
      onFirstDataRendered: props.onFirstDataRendered,
    }),
    [
      props.onRowSelected,
      props.onCellClicked,
      props.onRowClicked,
      props.onColumnMoved,
      props.onFilterOpened,
      props.tabToNextCell,
      props.onFirstDataRendered,
    ],
  );

// Using lodash isEqual for deeper comparison
const arePropsEqual = (prevProps, nextProps) => isEqual(prevProps, nextProps);

const GridComponent = memo(
  ({
    scrollToId,
    gridRef,
    context,
    onGridReady,
    columnDefs,
    handleClosePopup,
    onRowSelected,
    onCellClicked,
    onRowClicked,
    onColumnMoved,
    onFilterOpened,
    tabToNextCell,
    onFirstDataRendered,
    onBodyScroll,
  }) => {
    const dispatch = useDispatch();
    const firstMountRef = useRef(null);
    const previousParamsRef = useRef(null);
    const isHeadersChange = useSelector(isHeadersLanguageChange);
    const direction = useSelector(selectDirection);
    const directionChanged = useSelector(selectDirectionChanged);
    const rbSettings = useSelector(selectRB);

    const { data: isRTLEnabled } = useFeatureFlagQuery({
      flagKey: FEATURE_FLAGS_KEYS.RTL_ENABLED,
      defaultValue: false,
    });
    const { of } = useTexts();
    const { data: isLastRowWithData } = useIsLastRowWithData();
    const { performanceMeasureEventReport, getEventName } = usePerformanceMeasure();
    const { handleColumnResized } = useCacheColumnsWidth();
    const { data: isSROpenedFromQueue } = useFeatureFlagQuery({
      flagKey: FEATURE_FLAGS_KEYS.OPEN_SR_FROM_QUEUE,
      defaultValue: false,
    });
    const rbTexts = useRbTexts();

    // Memoize more computed values
    const isRTLMemo = useMemo(() => direction === 'rtl' && isRTLEnabled, [direction, isRTLEnabled]);

    const { localeText, languageChange } = useMemo(
      () => ({
        localeText: {
          to: '-',
          of,
          more: '',
        },
        languageChange: `grid-${JSON.stringify(rbSettings?.resourceBundleMap)}`,
      }),
      [of, rbSettings?.resourceBundleMap],
    );

    const onPaginationChanged = useCallback(() => {
      if (gridRef.current?.api) {
        const checkPageParams = isOneStepToNextPage({
          currentPage: gridRef.current.api.paginationGetCurrentPage() + 1,
          totalPages: gridRef.current.api.paginationGetTotalPages(),
          pageSize: gridRef.current.api.paginationGetPageSize(),
          blockSize: GRID.BLOCK_SIZE,
        });

        if (
          previousParamsRef.current?.fetchNextPage !== checkPageParams.fetchNextPage &&
          checkPageParams.fetchNextPage
        ) {
          previousParamsRef.current = checkPageParams;
          dispatch(setPrefetchSrParams(checkPageParams));
        }
      }
    }, [gridRef, dispatch]);

    const updateRowCountDisplay = (api) => {
      const rowCount = api.getInfiniteRowCount();
      const rowCountEl = document.getElementById('pagination_row_count');

      if (rowCountEl) {
        rowCountEl.innerHTML = String(rowCount);
      }

      if (rowCount > 0) {
        api.hideOverlay();
      } else {
        api.showNoRowsOverlay();
      }
    };

    const updateLoadingStates = () => {
      const centerCols = document.querySelector('.ag-center-cols-container');
      const leftCols = document.querySelector('.ag-pinned-left-cols-container');
      const rowId = document.querySelector('.rowIDelem');
      const noData = document.getElementById('grid_no_data_container');

      if (centerCols) {
        if (rowId?.textContent) {
          centerCols.classList.add('queueLoaded');
        } else if (leftCols) {
          centerCols.classList.remove('queueLoaded');
        }
      }

      if (noData) {
        noData.classList?.add('queueLoaded');
      }
    };

    const setupPaginationCounter = () => {
      const summaryPanel = document.querySelector('.ag-paging-row-summary-panel');
      if (summaryPanel?.querySelector('#pagination_row_count')) return;

      const counter = document.createElement('span');
      counter.className = 'ag-paging-row-summary-panel-number';
      counter.id = 'pagination_row_count';
      summaryPanel?.appendChild(counter);
    };

    const defaultColDef = useMemo(
      () => ({
        filter: 'agTextColumnFilter',
        resizable: true,
        lockPinned: true,
        suppressSizeToFit: 'false',
        filterParams: {
          suppressAndOrCondition: true,
          filterOptions: [COLUMN_FILTER_TYPES.equals, COLUMN_FILTER_TYPES.contains],
          handleClosePopup,
        },
        cellRenderer: DefaultCellRender,
        cellStyle: isSROpenedFromQueue ? { cursor: 'pointer' } : {},
      }),
      [handleClosePopup, isSROpenedFromQueue],
    );

    const onGridReadyHandler = useCallback(
      (params) => {
        if (!params?.api) return;

        const { api } = params;

        api.showLoadingOverlay();
        onGridReady?.(params);
        resizeLastPinnedColumn(params);

        api.addEventListener('modelUpdated', () => {
          setupPaginationCounter();
          updateRowCountDisplay(api);
          updateLoadingStates();
        });
      },
      [onGridReady],
    );

    const getColumnDraggIcon = useCallback(() => {
      const domNode = document.createElement('div');
      const root = createRoot(domNode);
      root.render(<ColumnDragIcon />);
      return domNode;
    }, []);

    const handlePopupAlignment = useCallback((params) => {
      const closestDivWidth = params.eventSource.closest('div').offsetWidth;
      const ePopup = params.ePopup;

      ePopup.style.top = '64px';

      let oldLeftStr = ePopup.style.left;
      oldLeftStr = oldLeftStr.substring(0, oldLeftStr.indexOf('px'));

      const oldLeft = parseInt(oldLeftStr, 10);
      const newLeft = oldLeft - closestDivWidth;
      ePopup.style.left = `${newLeft}px`;
    }, []);

    const isFullWidthRow = useCallback(
      (params) => {
        if (params.rowNode?.rowIndex === GRID.MAX_ROWS_ALLOWED_NUM - 1) {
          if (isLastRowWithData) {
            return params.api.getInfiniteRowCount() > GRID.BLOCK_SIZE;
          }
          return true;
        }
      },
      [isLastRowWithData],
    );

    const onCellMouseOver = useCallback(
      (e) => {
        if (Array.isArray(e?.data)) {
          const srId = e?.data?.find((col) => col.field === 'id')?.value;
          const title = e?.data?.find((col) => col.field === 'title')?.value;
          dispatch(setSrDetailsForSummary({ srId, title }));
        }
      },
      [dispatch],
    );

    const getRowId = useCallback(({ data }) => {
      if (data.length > 0) {
        return data.find((sr) => sr?.field === 'id')?.value;
      }
    }, []);

    const onCellEditingStarted = useCallback(
      (e) => {
        onCellClicked(e?.node?.id);
      },
      [onCellClicked],
    );

    const onCellEditingStopped = useCallback(() => {
      onCellClicked(null);
    }, [onCellClicked]);

    useEffect(() => {
      firstMountRef.current = true;
    }, []);

    const modules = useMemo(() => [InfiniteRowModelModule], []);

    const rowClassRules = useMemo(
      () => ({
        'shadow-inset-center': (params) => params.node.id === parseInt(scrollToId, 10),
      }),
      [scrollToId],
    );

    const components = useMemo(
      () => ({
        agColumnHeader: CustomColumnHeader,
        ActionBtnRenderer,
      }),
      [],
    );

    const normalizedColumnDefs = useMemo(() => {
      const columnDefsArray = !Array.isArray(columnDefs) ? [] : columnDefs;
      const translatedColumnDefsArray = columnDefsArray.map((item) => {
        const headerName = getCustomHeaderName(rbTexts, item?.field) ?? item?.headerName;
        return { ...item, headerName };
      });
      return translatedColumnDefsArray;
    }, [columnDefs, rbTexts]);

    const icons = useMemo(
      () => ({
        columnMoveMove: getColumnDraggIcon,
        columnDrag: getColumnDraggIcon,
        columnMovePin: getColumnDraggIcon,
        columnMoveRight: getColumnDraggIcon,
        columnMoveLeft: getColumnDraggIcon,
      }),
      [getColumnDraggIcon],
    );

    const getRowClass = useCallback((params) => {
      if (params.data === undefined) {
        return 'ag-empty-row';
      }
    }, []);

    useEffect(() => {
      if (columnDefs?.length && gridRef?.current) {
        resizeLastPinnedColumn(gridRef?.current);
      }
    }, [columnDefs, gridRef]);

    useEffect(() => {
      if (gridRef?.current?.api && isHeadersChange) {
        gridRef.current.api.setColumnDefs(columnDefs);
        gridRef.current.api.refreshHeader();
        dispatch(toggleSwitchHeadersLanguage(false));
      }
    }, [columnDefs, dispatch, gridRef, isHeadersChange]);

    useEffect(() => {
      const api = gridRef.current?.api;
      const currentLocation = api?.paginationGetCurrentPage();
      const lastLocation = Number(localStorage.getItem('currentPage'));
      if (!Number.isNaN(lastLocation) && currentLocation !== lastLocation) {
        api?.paginationGoToPage(lastLocation);
      }

      return () => {
        if (api) {
          const currentPage = api?.paginationGetCurrentPage() ?? 0;
          localStorage.setItem('currentPage', String(currentPage));
          api.cleanDownReferencesToAvoidMemoryLeakInCaseApplicationIsKeepingReferenceToDestroyedGrid();
        }
      };
    }, [gridRef]);

    const handlePerformanceReport = useCallback(() => {
      performance.mark(PERFORMANCE_MEASURE.QUEUE.DATA_POPULATION);
      const { eventName, startEventName, data = {} } = getEventName();
      if (eventName && startEventName) {
        const startTime = performance.getEntriesByName(startEventName)[0]?.startTime;
        const endTime = performance.getEntriesByName(PERFORMANCE_MEASURE.QUEUE.DATA_POPULATION)[0]?.startTime;
        const duration = endTime - startTime + GRID.RENDER_DATA_TIME;
        performanceMeasureEventReport(eventName, {
          tenantId: localStorage.getItem(APP_CONSTANTS.ACCOUNT_ID_LOCAL_KEY),
          duration,
          ...data,
        });
      } else {
        performance.clearMarks(PERFORMANCE_MEASURE.QUEUE.DATA_POPULATION);
      }
    }, [getEventName, performanceMeasureEventReport]);

    const onColumnResized = useCallback(
      (params) => {
        handleColumnResized(params);
      },
      [handleColumnResized],
    );

    const onFilterChanged = useCallback(() => {
      handlePerformanceReport();
    }, [handlePerformanceReport]);

    const onSortChanged = useCallback(() => {
      handlePerformanceReport();
    }, [handlePerformanceReport]);

    const handleFirstDataRendered = useCallback(
      (params) => {
        handlePerformanceReport();
        processFirstDataRendered(params, onFirstDataRendered);
      },
      [handlePerformanceReport, onFirstDataRendered],
    );

    // Get base handlers
    const baseHandlers = useGridHandlers({
      onRowSelected,
      onCellClicked,
      onRowClicked,
      onColumnMoved,
      onFilterOpened,
      tabToNextCell,
      onFirstDataRendered,
    });

    // Combine all handlers
    const eventHandlers = useMemo(
      () => ({
        onPaginationChanged,
        onColumnResized,
        onFilterChanged,
        onSortChanged,
        onCellMouseOver,
        onBodyScroll,
        onCellEditingStarted,
        onCellEditingStopped,
        onGridReady: onGridReadyHandler,
        onFirstDataRendered: handleFirstDataRendered,
        ...baseHandlers,
      }),
      [
        onPaginationChanged,
        onColumnResized,
        onFilterChanged,
        onSortChanged,
        onCellMouseOver,
        onCellEditingStarted,
        onCellEditingStopped,
        onGridReadyHandler,
        handleFirstDataRendered,
        baseHandlers,
        onBodyScroll
      ],
    );

    // Memoize static configuration
    const staticConfig = useMemo(
      () => ({
        rowSelection: 'multiple',
        blockLoadDebounceMillis: 300,
        suppressRowClickSelection: true,
        rowModelType: 'infinite',
        suppressAnimationFrame: true,
        maxConcurrentDatas: 10,
        suppressDragLeaveHidesColumns: true,
        suppressPropertyNamesCheck: true,
        suppressColumnVirtualisation: true,
        reactiveCustomComponents: true,
        headerHeight: 44,
        rowHeight: 44,
        pagination: true,
        paginationAutoPageSize: true,
        cacheBlockSize: GRID.BLOCK_SIZE,
        sortingOrder: SORTING_ORDER,
      }),
      [],
    );

    // Add cleanup effect
    useEffect(
      () => () => {
        // The grid should be destroyed by now, but remove listener just to be safe
        if (gridRef.current?.api) {
          gridRef?.current?.api?.removeEventListener('modelUpdated');
        }
      },
      [gridRef],
    );

    // Add row cleanup handler
    const handleRowRemoved = useCallback((event) => {
      if (event.node?.rowCtrl?.centerGui) {
        event.node.rowCtrl.centerGui.destroy();
        event.node.rowCtrl.centerGui = null;
      }
    }, []);

    // Memoize grid props
    const gridProps = useMemo(
      () => ({
        debug: false,
        localeText,
        enableRtl: Boolean(isRTLMemo),
        modules,
        ref: gridRef,
        columnDefs: normalizedColumnDefs,
        defaultColDef,
        context,
        rowClassRules,
        components,
        icons,
        isFullWidthRow,
        fullWidthCellRenderer: EndResultsMessage,
        noRowsOverlayComponent: EndResultsMessage,
        loadingOverlayComponent: LoadingOverlayMessage,
        tabToNextCell,
        navigateToNextCell: tabToNextCell,
        getRowId,
        getRowClass,
        postProcessPopup: handlePopupAlignment,
        onRowRemoved: handleRowRemoved,
        ...eventHandlers,
        ...staticConfig,
      }),
      [
        localeText,
        isRTLMemo,
        modules,
        gridRef,
        normalizedColumnDefs,
        defaultColDef,
        context,
        rowClassRules,
        components,
        icons,
        isFullWidthRow,
        tabToNextCell,
        getRowId,
        getRowClass,
        handlePopupAlignment,
        handleRowRemoved,
        eventHandlers,
        staticConfig,
      ],
    );

    if (directionChanged) {
      setTimeout(() => dispatch(setDirectionChanged(false)), 3000);
      return null;
    }
    return <AgGridReact key={languageChange} {...gridProps} />;
  },
  arePropsEqual,
);

GridComponent.propTypes = {
  scrollToId: PropTypes.string,
  gridRef: PropTypes.object.isRequired,
  context: PropTypes.object,
  onGridReady: PropTypes.func.isRequired,
  columnDefs: PropTypes.array.isRequired,
  handleClosePopup: PropTypes.func.isRequired,
  onRowSelected: PropTypes.func,
  onCellClicked: PropTypes.func,
  onRowClicked: PropTypes.func,
  handleColumnMoved: PropTypes.func,
  onFilterOpened: PropTypes.func,
  tabToNextCell: PropTypes.func,
  onFirstDataRendered: PropTypes.func,
  onBodyScroll: PropTypes.func,
};

export default GridComponent;
